import React, { Component } from 'react';
import axios from 'axios';
import { Elements, StripeProvider } from 'react-stripe-elements';
import { Typography, withStyles, CircularProgress } from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import TopUpForm from '../components/TopUpForm';
import { contactOperatorString } from '../helpers/contact';
import { waitForObjectElement } from '../helpers/wait';

const styles = {
    root: {
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative'
    },
    error: {
        margin: '20px 0 0 5px',
        color: red[500],
        minHeight: '20px',
        textAlign: 'center'
    },
    spinner: {
        marginLeft: 'calc(50% - 22px)',
        marginTop: '60px'
    }
};

const hasExpired = ticket => {
    const now = new Date().toISOString();
    return ticket.expiry && ticket.expiry < now;
};

const isVoided = ticket => {
    return ticket.ticket_status === 'void';
};

class TopUp extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: '',
            ticket: null,
            customerTicket: null,
            client: null,
            isLoading: true
        };
    }

    async componentDidMount() {
        const {
            match: {
                params: { ticketId }
            }
        } = this.props;

        try {
            const currentSession = await Auth.currentSession();

            // get customer ticket
            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const customerTicketResponse = await axios.get(
                `${
                    process.env.REACT_APP_API_ENDPOINT
                }/customer-ticket/user-id/${
                    currentSession.getAccessToken().payload.sub
                }/${ticketId}`,
                {
                    headers
                }
            );

            const customerTicket = customerTicketResponse.data;

            // get client
            const clientResponse = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/client/${customerTicket.client_id}`,
                {
                    headers
                }
            );
            const client = clientResponse.data;

            // get ticket
            const ticketResponse = await axios.get(
                `${process.env.REACT_APP_TICKET_API_ENDPOINT}/ticket/${customerTicket.ticket_id}`,
                {
                    headers
                }
            );
            const ticket = ticketResponse.data;

            // Wait for stripe to be loaded
            try {
                await waitForObjectElement(window, 'Stripe');
            } catch (stripeError) {
                console.error(stripeError);

                this.setState({
                    ticket,
                    client,
                    customerTicket,
                    isLoading: false,
                    error:
                        'The payment system is currently unavailable. Please try again later or contact our support team.'
                });
                return;
            }

            this.setState({
                ticket,
                client,
                customerTicket,
                isLoading: false,
                error: ''
            });
        } catch (e) {
            console.error(e);

            const errMessage =
                e.response && e.response.status === 403
                    ? 'You do not have permission to top-up this ticket. Please log into the original account which purchased the ticket. Hint: Please use the email address where your top-up alert was sent to.'
                    : 'There was a problem retrieving the ticket. Please try again later or contact our support team.';

            this.setState({
                isLoading: false,
                error: errMessage
            });
        }
    }

    render() {
        const { client, customerTicket, ticket, error, isLoading } = this.state;

        const { classes } = this.props;

        if (isLoading) {
            return (
                <div className={classes.root}>
                    <CircularProgress
                        id="loading-spinner"
                        className={classes.spinner}
                    />
                </div>
            );
        }

        if (error) {
            return (
                <div className={classes.root}>
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                </div>
            );
        }

        if (hasExpired(customerTicket)) {
            return (
                <Typography
                    className={classes.error}
                    variant="body2"
                    style={{ marginBottom: '20px' }}
                >
                    This ticket has expired and can no longer be topped up or
                    used.
                </Typography>
            );
        }

        if (isVoided(customerTicket)) {
            return (
                <Typography
                    className={classes.error}
                    variant="body2"
                    style={{ marginBottom: '20px' }}
                >
                    This ticket has been voided and cannot be topped up.
                </Typography>
            );
        }

        const tripPrice =
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_trip_prices &&
            customerTicket.zone
                ? ticket.zone_trip_prices[customerTicket.zone]
                : ticket.trip_price;

        if (ticket.pricing_model === 'Zonal' && !tripPrice) {
            return (
                <Typography
                    className={classes.error}
                    variant="body2"
                    style={{ marginBottom: '20px' }}
                >
                    The top-up facility for your flexi ticket is unavailable
                    because pricing for your zone has been removed. Your
                    transport provider can resolve this by configuring a price
                    for your specified zone. Please contact{' '}
                    {contactOperatorString(client)} for more information.
                </Typography>
            );
        }

        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUB_KEY, {
            stripeAccount: client.stripe_account_id
        });

        return (
            <div className={classes.root}>
                <StripeProvider stripe={stripe}>
                    <Elements
                        fonts={[
                            {
                                cssSrc:
                                    'https://fonts.googleapis.com/css?family=Roboto:400,500'
                            }
                        ]}
                    >
                        <TopUpForm
                            client={client}
                            customerTicket={customerTicket}
                            ticket={ticket}
                            tripPrice={tripPrice}
                        />
                    </Elements>
                </StripeProvider>
            </div>
        );
    }
}

export default withStyles(styles)(TopUp);
